import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowRightLeft, Ban, BookOpen, Check, Copy, Globe2, KeyRound, Loader2,
  Plus, RadioTower, RefreshCw, Server, Trash2, TriangleAlert, UploadCloud,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { notifySettingsUpdated } from '../context/SettingsContext';
import HelpDrawer from './components/HelpDrawer';
import EntryDomainsGuide from './components/EntryDomainsGuide';
import EntryDomainsStatus from './components/EntryDomainsStatus';
import ConfirmDialog from './components/ConfirmDialog';

/**
 * Operasyon → Giriş Adresleri.
 *
 * Kurulumun oyuncuya açık adresi engellenince yenisine geçilir
 * (`betrun301` → `betrun302` → `betrun303`). Bu ekran o listeyi ve geçişi
 * panele taşır: adresler yazılır, biri **yayına** alınır ve eskisine gelen
 * ziyaretçi yenisine yönlendirilir.
 *
 * Adres defteri (Alan Adları) ile karıştırılmamalıdır: orası hiç değişmemesi
 * gereken sabit adreslerin rol haritasıdır, burası ise sık değişen giriş
 * adresidir. Ekranın üstündeki not bunu ayrıca söyler.
 *
 * Yayına alma lisans sunucusuna da bildirilir: lisans bu adreslere bağlıysa
 * yeni adres lisansa tanımlanır, sabit bir adrese bağlıysa lisans değişmez.
 * Lisans panelinde yapılan geçiş de bir sonraki lisans kontrolünde buraya
 * gelir.
 *
 * Yeni adresin sunucuda çalışması da buradan yapılır: sunucu bilgileri (SSH)
 * bir kez girilir, "Sunucuya kur" kurulumun çalışan site tanımını yeni ada
 * kopyalar ve isteğe bağlı olarak sertifika alır. İş arka planda yürür;
 * ekran sürerken durumu birkaç saniyede bir yoklar.
 *
 * Geçişi kendi Cloudflare Worker'ında yapan kurulumun ucu (`entry_domain.read`)
 * bu ekranda anlatılmaz: anahtarı, dokümanı ve uç adresi tek yerde, Entegrasyon
 * API ekranında durur.
 */

const emptyState = {
  enabled: false,
  redirect_enabled: false,
  show_list: false,
  notice: '',
  active_id: '',
  active_host: '',
  active_url: '',
  current_host: '',
  domains: [],
  license: {},
  license_configured: false,
  license_message: '',
  server: { configured: false },
  server_jobs: {},
  closed_hosts: [],
  gateway_host: '',
};

const emptyServerDraft = {
  host: '127.0.0.1',
  port: 22,
  username: 'root',
  auth: 'password',
  password: '',
  private_key: '',
  passphrase: '',
  sudo_password: '',
  ssl_email: '',
};

const JOB_LABELS = {
  running: 'Sunucuya kuruluyor…',
  done: 'Sunucuda kurulu',
  failed: 'Sunucu kurulumu başarısız',
  removed: 'Sunucudan kaldırıldı',
};

const formatTime = (value) => {
  if (!value) return '';
  // Kayıt naive UTC tutar (bkz. CLAUDE.md); yerel saate çevrilerek yazılır.
  const date = new Date(value.endsWith('Z') ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('tr-TR');
};

const KIND_LABELS = {
  mirror: 'Giriş adresi',
  gateway: 'Hızlı Giriş',
};

const EntryDomains = () => {
  const { toast } = useToast();
  const [state, setState] = useState(emptyState);
  const [draft, setDraft] = useState({ host: '', label: '', kind: 'mirror' });
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [checks, setChecks] = useState({});
  const [serverDraft, setServerDraft] = useState(emptyServerDraft);
  const [serverOpen, setServerOpen] = useState(false);
  const [withSsl, setWithSsl] = useState(true);
  const [autoProvision, setAutoProvision] = useState(true);
  const [openLog, setOpenLog] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  // Sil / Sunucudan kaldır onayı: { action: 'delete' | 'unprovision', row }.
  const [pending, setPending] = useState(null);
  const [closeOnDelete, setCloseOnDelete] = useState(true);

  const absorb = useCallback((data) => {
    setState({ ...emptyState, ...data });
    setNotice(data.notice || '');
    if (data.check) {
      setChecks((current) => ({ ...current, [data.check.id]: data.check }));
    }
  }, []);

  // Sunucu formu kayıttan doldurulur; sırlar hiçbir zaman geri gelmez.
  useEffect(() => {
    const server = state.server || {};
    setServerDraft((draft) => ({
      ...draft,
      host: server.host || draft.host,
      port: server.port || draft.port,
      username: server.username || draft.username,
      auth: server.auth || draft.auth,
      ssl_email: server.ssl_email ?? draft.ssl_email,
      password: '',
      private_key: '',
      passphrase: '',
      sudo_password: '',
    }));
  }, [state.server]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/entry-domains');
      absorb(data);
    } catch (error) {
      toast({
        title: 'Giriş adresleri okunamadı',
        description: error.response?.data?.detail || 'Bir hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [absorb, toast]);

  useEffect(() => { load(); }, [load]);

  // Arka planda süren sunucu kurulumu varken durum birkaç saniyede bir yoklanır.
  const running = Object.values(state.server_jobs || {})
    .some((job) => job.state === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await client.get('/admin/entry-domains');
        absorb(data);
      } catch {
        // Bir yoklama düşerse sıradaki dener.
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [running, absorb]);

  const run = async (key, request, successTitle) => {
    setBusy(key);
    try {
      const { data } = await request();
      absorb(data);
      // Portal yönlendirme kararını /settings yanıtından okur; yenilenmezse
      // yönetici kendi sekmesinde eski adresi görmeye devam eder.
      notifySettingsUpdated();
      if (successTitle) toast({ title: successTitle });
      return data;
    } catch (error) {
      toast({
        title: 'İşlem tamamlanamadı',
        description: error.response?.data?.detail || 'Bir hata oluştu',
        variant: 'destructive',
      });
      return null;
    } finally {
      setBusy('');
    }
  };

  const patchSettings = (values, successTitle) => run(
    'settings', () => client.put('/admin/entry-domains', values), successTitle,
  );

  const provision = (row) => run(
    `provision-${row.id}`,
    () => client.post(`/admin/entry-domains/${row.id}/provision`, { ssl: withSsl }),
    `${row.host} sunucuya kuruluyor`,
  );

  // Kapatmanın sonucu (sunucu tanımı yazıldı mı, yalnızca uygulama mı
  // kapattı) yöneticiye olduğu gibi söylenir.
  const closedToast = (data, title) => {
    if (!data) return;
    toast({ title, description: data.closed?.message || undefined });
  };

  const unprovision = async (row) => {
    const data = await run(
      `unprovision-${row.id}`,
      () => client.post(`/admin/entry-domains/${row.id}/unprovision`),
    );
    closedToast(data, `${row.host} kapatıldı`);
  };

  const addDomain = async () => {
    const host = draft.host.trim();
    const before = new Set(state.domains.map((item) => item.id));
    const data = await run(
      'add',
      () => client.post('/admin/entry-domains', {
        host, label: draft.label.trim(), kind: draft.kind,
      }),
      'Giriş adresi eklendi',
    );
    if (!data) return;
    setDraft({ host: '', label: '', kind: 'mirror' });
    if (autoProvision && data.server?.configured) {
      // Sunucu adresi normalize eder (şema, büyük harf); yeni satır kimliğinden bulunur.
      const row = (data.domains || []).find((item) => !before.has(item.id));
      if (row) provision(row);
    }
  };

  const saveServer = () => {
    const body = { ...serverDraft, port: Number(serverDraft.port) || 22 };
    // Boş sır alanı "değiştirme" demektir; hiç gönderilmez.
    ['password', 'private_key', 'passphrase', 'sudo_password'].forEach((key) => {
      if (!body[key]) delete body[key];
    });
    return run('server-save', () => client.put('/admin/entry-domains/server', body),
      'Sunucu bilgileri kaydedildi');
  };

  // Sunucudan alınan adreslerin özeti; boşsa neden boş olduğu da söylenir.
  const importSummary = (imported) => {
    if (!imported) return '';
    if (!imported.ok) return `Adresler alınamadı: ${imported.message}`;
    const parts = [];
    if (imported.added?.length) parts.push(`${imported.added.length} adres listeye eklendi`);
    else if (imported.found?.length) parts.push('Sunucudaki adreslerin hepsi zaten listede');
    else parts.push('Sunucuda bu kuruluma ait bir site tanımı bulunamadı');
    if (imported.activated) parts.push(`${imported.activated} yayındaki adres olarak işaretlendi`);
    return `${parts.join('; ')}.`;
  };

  const testServer = async () => {
    const data = await run('server-test', () => client.post('/admin/entry-domains/server/test'));
    if (!data) return;
    toast(data.server_test?.ok
      ? {
        title: 'Bağlantı başarılı',
        description: [data.server_test.message, importSummary(data.server_import)]
          .filter(Boolean).join(' '),
      }
      : { title: 'Bağlantı kurulamadı', description: data.server_test?.message, variant: 'destructive' });
  };

  const importFromServer = async () => {
    const data = await run('server-import', () => client.post('/admin/entry-domains/server/import'));
    if (data) toast({ title: 'Sunucudaki adresler', description: importSummary(data.server_import) });
  };

  const forgetServer = () => run(
    'server-forget', () => client.delete('/admin/entry-domains/server'),
    'Sunucu bilgileri silindi',
  );

  const activate = async (row) => {
    const data = await run(
      `activate-${row.id}`,
      () => client.post(`/admin/entry-domains/${row.id}/activate`),
    );
    if (!data) return;
    const lic = data.license || {};
    if (lic.error && data.license_configured) {
      toast({
        title: `${row.host} yayına alındı`,
        description: data.license_message,
      });
    } else {
      toast({
        title: `${row.host} yayına alındı`,
        description: lic.follows === true
          ? 'Lisans da yeni adrese taşındı.'
          : undefined,
      });
    }
  };

  const syncLicense = () => run(
    'license-sync',
    () => client.post('/admin/entry-domains/license-sync'),
  ).then((data) => {
    if (!data) return;
    toast(data.license?.error
      ? { title: 'Eşitlenemedi', description: data.license_message, variant: 'destructive' }
      : { title: 'Lisansla eşitlendi' });
  });

  const setStatus = (row, status) => run(
    `status-${row.id}`,
    () => client.patch(`/admin/entry-domains/${row.id}`, { status }),
    status === 'blocked' ? 'Adres engellendi olarak işaretlendi'
      : 'Adres yeniden kullanılabilir',
  );

  const check = (row) => run(
    `check-${row.id}`, () => client.post(`/admin/entry-domains/${row.id}/check`),
  );

  const removeDomain = async (row, close) => {
    const data = await run(
      `del-${row.id}`,
      () => client.delete(`/admin/entry-domains/${row.id}`, { params: { close } }),
    );
    closedToast(data, close
      ? `${row.host} sunucudan kaldırıldı ve listeden silindi`
      : 'Adres listeden silindi');
  };

  const askDelete = (row) => {
    // Yayındaki ve panelin açık olduğu adres kapatılamaz; seçenek kapalı gelir.
    setCloseOnDelete(row.id !== state.active_id && row.host !== state.current_host);
    setPending({ action: 'delete', row });
  };

  const confirmPending = async () => {
    if (!pending) return;
    const { action, row } = pending;
    setPending(null);
    if (action === 'unprovision') await unprovision(row);
    else await removeDomain(row, closeOnDelete);
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Kopyalandı' });
    } catch {
      toast({ title: 'Kopyalanamadı', variant: 'destructive' });
    }
  };

  const header = (
    <div className="admin-page-header">
      <div>
        <h1>Giriş Adresleri</h1>
        <p>
          Kurulumun yedek alan adları ve aralarında geçiş. Yayındaki adres
          engellenince buradan sıradakine geçilir; eski adreste kalan
          ziyaretçi yenisine taşınır.
        </p>
      </div>
      <button
        type="button"
        className="admin-btn admin-btn-secondary shrink-0"
        onClick={() => setHelpOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={helpOpen}
      >
        <BookOpen size={15} aria-hidden="true" /> Kullanım Kılavuzu
      </button>
      {/* Üstte kurulumun o anki durumu, altında adres ekleme ve değiştirmenin
          kısa yolu; her düğmenin ayrıntısı kılavuzun tamamındadır. */}
      <HelpDrawer
        open={helpOpen}
        title="Giriş Adresleri"
        subtitle="Alan adı nasıl eklenir, engellenince nasıl değiştirilir."
        onClose={() => setHelpOpen(false)}
      >
        {!loading && <EntryDomainsStatus state={state} />}
        <EntryDomainsGuide />
      </HelpDrawer>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-page">
        {header}
        <div className="admin-card admin-state">
          <Loader2 size={26} className="animate-spin text-slate-400" />
          <h2>Giriş adresleri yükleniyor…</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page flex flex-col gap-5">
      {header}

      {/* `is-live` yalnızca yeni tasarımda öne çıkarılır (admin-v2.css). */}
      <div className="admin-card address-card is-live">
        <h2 className="admin-card-title">
          <RadioTower size={16} /> Yayındaki adres
        </h2>
        {state.active_host ? (
          <div className="address-row">
            <div>
              <div className="address-host">{state.active_host}</div>
              <div className="address-meta">
                Oyuncular bu adrese yönlendirilir.
                {state.current_host === state.active_host
                  && ' Şu anda bu adrestesiniz.'}
              </div>
            </div>
            <div className="address-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => copy(state.active_url)}
              >
                <Copy size={14} /> Adresi kopyala
              </button>
            </div>
          </div>
        ) : (
          <p className="admin-card-hint">
            Henüz bir adres yayına alınmadı. Aşağıdaki listeden birini
            <strong> Yayına al</strong> ile seçin. Seçilene kadar listedeki
            adresler siteyi açmaz; ziyaretçi "bu adres henüz yayında değil"
            ekranını görür.
          </p>
        )}

        <p className="admin-card-hint">
          <strong>Yayına alınmayan adres siteyi açmaz.</strong> Listedeki diğer
          adreslere gelen ziyaretçi her zaman yayındaki adrese taşınır; bu bir
          ayar değildir. <strong>Engellenen</strong> adres ise taşımaz,
          &quot;bu adres artık kullanılmıyor&quot; ekranını gösterir;
          <em>Kullanılabilir</em>&apos;e basınca hemen yeniden çalışır. Panel (<code>/admin</code>) bu kuraldan muaftır, Alan
          Adları ekranındaki adresler etkilenmez.
        </p>

        <div className="address-actions-row flex-col items-start">
          <label className={`admin-switch${state.enabled ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={state.enabled}
              disabled={busy === 'settings'}
              onChange={(e) => patchSettings(
                { enabled: e.target.checked },
                e.target.checked ? 'Giriş adresi duyurusu açıldı'
                  : 'Giriş adresi duyurusu kapatıldı',
              )}
            />
            <span className="admin-switch-track" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">Giriş adresini oyuncuya duyur</span>
              <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">
                Açıkken sitenin alt kısmında (footer) yayındaki adres ve aşağıdaki not yazar.
              </span>
            </span>
          </label>
          <label className={`admin-switch${state.show_list ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={state.show_list}
              disabled={busy === 'settings' || !state.enabled}
              onChange={(e) => patchSettings({ show_list: e.target.checked })}
            />
            <span className="admin-switch-track" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">Adres listesini oyuncuya da göster</span>
              <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">
                Açıkken sitenin alt kısmındaki (footer) giriş adresi bandında yedek adresler
                de yazar. Kapalıyken yalnızca panelde durur; erkenden ifşa edilmez.
              </span>
            </span>
          </label>
        </div>

        <div className="address-field">
          <label className="admin-label" htmlFor="entry-notice">
            Footer'da yazan not (isteğe bağlı)
          </label>
          <p className="admin-card-hint mt-0 mb-2">
            Doluysa sitenin her sayfasının alt kısmında, yayındaki adresle birlikte
            yazar: <em>"{notice || 'Güncel giriş adresimiz'}: {state.active_host || 'ornek302.com'}"</em>.
            Not boş ve liste kapalıysa bant hiç görünmez. "Giriş adresini
            oyuncuya duyur" açık olmalıdır.
          </p>
          <input
            id="entry-notice"
            className="admin-input admin-input-w-lg"
            value={notice}
            maxLength={200}
            onChange={(e) => setNotice(e.target.value)}
            onBlur={() => {
              if (notice !== (state.notice || '')) {
                patchSettings({ notice }, 'Not kaydedildi');
              }
            }}
            placeholder="Güncel giriş adresimiz"
          />
        </div>
      </div>

      <div className="admin-card address-card">
        <h2 className="admin-card-title">
          <Server size={16} /> Sunucu bağlantısı
        </h2>
        <p className="admin-card-hint">
          Yeni giriş adresinin sunucuda çalışması için nginx site tanımı ve
          sertifika gerekir. Sunucu bilgilerini bir kez girin; her adresin
          yanındaki <strong>Sunucuya kur</strong> düğmesi bu işi sizin yerinize
          yapar. Sitenin çalışan tanımı yeni adrese kopyalanır, mevcut
          adreslere dokunulmaz. Parola şifreli saklanır ve bir daha gösterilmez.
          Bağlantı testi başarılı olunca sunucuda zaten tanımlı adresler
          listeye kendiliğinden eklenir (panel ve bildirim adresleri hariç).
        </p>
        <div className="address-row">
          <div className="address-meta">
            {state.server?.configured ? (
              <>
                <strong>{state.server.username}@{state.server.host}:{state.server.port}</strong>
                {state.server.last_test_at && (
                  <>
                    {' · '}
                    {state.server.last_test_ok ? 'son test başarılı' : 'son test başarısız'}
                    {` (${formatTime(state.server.last_test_at)})`}
                  </>
                )}
                {state.server.last_test_message && !state.server.last_test_ok && (
                  <div className="mt-1 text-amber-700">{state.server.last_test_message}</div>
                )}
              </>
            ) : 'Sunucu bilgileri girilmedi.'}
          </div>
          <div className="address-actions">
            {state.server?.configured && (
              <button
                className="admin-btn admin-btn-secondary"
                onClick={importFromServer}
                disabled={busy === 'server-import'}
                title="Sunucuda bu siteye bakan nginx tanımlarındaki adresleri listeye ekler"
              >
                {busy === 'server-import'
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Globe2 size={14} />}
                {' '}Sunucudaki adresleri al
              </button>
            )}
            {state.server?.configured && (
              <button
                className="admin-btn admin-btn-secondary"
                onClick={testServer}
                disabled={busy === 'server-test'}
              >
                {busy === 'server-test'
                  ? <Loader2 size={14} className="animate-spin" />
                  : <RefreshCw size={14} />}
                {' '}Bağlantıyı test et
              </button>
            )}
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setServerOpen((open) => !open)}
              aria-expanded={serverOpen}
            >
              {state.server?.configured ? 'Bilgileri düzenle' : 'Bilgileri gir'}
            </button>
          </div>
        </div>

        {serverOpen && (
          <div className="address-form flex-wrap">
            <div className="address-field">
              <label className="admin-label" htmlFor="srv-host">Sunucu adresi</label>
              <input
                id="srv-host"
                className="admin-input"
                value={serverDraft.host}
                onChange={(e) => setServerDraft({ ...serverDraft, host: e.target.value })}
                placeholder="127.0.0.1"
                autoComplete="off"
              />
            </div>
            <div className="address-field">
              <label className="admin-label" htmlFor="srv-port">SSH portu</label>
              <input
                id="srv-port"
                type="number"
                inputMode="numeric"
                className="admin-input admin-input-w-xs"
                value={serverDraft.port}
                onChange={(e) => setServerDraft({ ...serverDraft, port: e.target.value })}
              />
            </div>
            <div className="address-field">
              <label className="admin-label" htmlFor="srv-user">Kullanıcı</label>
              <input
                id="srv-user"
                className="admin-input admin-input-w-sm"
                value={serverDraft.username}
                onChange={(e) => setServerDraft({ ...serverDraft, username: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="address-field">
              <label className="admin-label" htmlFor="srv-auth">Giriş yöntemi</label>
              <select
                id="srv-auth"
                className="admin-input admin-input-w-sm"
                value={serverDraft.auth}
                onChange={(e) => setServerDraft({ ...serverDraft, auth: e.target.value })}
              >
                <option value="password">Parola</option>
                <option value="key">SSH anahtarı</option>
              </select>
            </div>
            {serverDraft.auth === 'password' ? (
              <div className="address-field">
                <label className="admin-label" htmlFor="srv-pass">Parola</label>
                <input
                  id="srv-pass"
                  type="password"
                  className="admin-input"
                  value={serverDraft.password}
                  onChange={(e) => setServerDraft({ ...serverDraft, password: e.target.value })}
                  placeholder={state.server?.has_password ? 'Kayıtlı — değiştirmek için yazın' : ''}
                  autoComplete="new-password"
                />
              </div>
            ) : (
              <>
                <div className="address-field w-full">
                  <label className="admin-label" htmlFor="srv-key">Özel anahtar</label>
                  <textarea
                    id="srv-key"
                    className="admin-input font-mono text-xs"
                    rows={4}
                    value={serverDraft.private_key}
                    onChange={(e) => setServerDraft({ ...serverDraft, private_key: e.target.value })}
                    placeholder={state.server?.has_private_key
                      ? 'Kayıtlı — değiştirmek için yapıştırın'
                      : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                    spellCheck={false}
                  />
                </div>
                <div className="address-field">
                  <label className="admin-label" htmlFor="srv-phrase">Anahtar parolası (varsa)</label>
                  <input
                    id="srv-phrase"
                    type="password"
                    className="admin-input"
                    value={serverDraft.passphrase}
                    onChange={(e) => setServerDraft({ ...serverDraft, passphrase: e.target.value })}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}
            {serverDraft.username !== 'root' && (
              <div className="address-field">
                <label className="admin-label" htmlFor="srv-sudo">sudo parolası (farklıysa)</label>
                <input
                  id="srv-sudo"
                  type="password"
                  className="admin-input"
                  value={serverDraft.sudo_password}
                  onChange={(e) => setServerDraft({ ...serverDraft, sudo_password: e.target.value })}
                  autoComplete="new-password"
                />
              </div>
            )}
            <div className="address-field">
              <label className="admin-label" htmlFor="srv-mail">Sertifika e-postası (isteğe bağlı)</label>
              <input
                id="srv-mail"
                type="email"
                className="admin-input"
                value={serverDraft.ssl_email}
                onChange={(e) => setServerDraft({ ...serverDraft, ssl_email: e.target.value })}
                placeholder="ornek@site.com"
              />
            </div>
            <div className="flex flex-wrap gap-2 w-full">
              <button
                className="admin-btn admin-btn-primary"
                onClick={saveServer}
                disabled={busy === 'server-save' || !serverDraft.host.trim()}
              >
                Kaydet
              </button>
              {state.server?.configured && (
                <button
                  className="admin-btn admin-btn-danger"
                  onClick={forgetServer}
                  disabled={busy === 'server-forget'}
                >
                  <Trash2 size={14} /> Bilgileri sil
                </button>
              )}
            </div>
          </div>
        )}

        {state.server?.configured && (
          <div className="address-actions-row flex-col items-start">
            <label className={`admin-switch${withSsl ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={withSsl}
                onChange={(e) => setWithSsl(e.target.checked)}
              />
              <span className="admin-switch-track" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">Kurarken SSL sertifikası al</span>
                <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">
                  Let's Encrypt ile. Alan adının DNS kaydı hazır olmalıdır; alınamazsa site HTTP ile kurulur.
                </span>
              </span>
            </label>
            <label className={`admin-switch${autoProvision ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={autoProvision}
                onChange={(e) => setAutoProvision(e.target.checked)}
              />
              <span className="admin-switch-track" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">Eklenen adresi sunucuya da kur</span>
                <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">
                  Açıkken listeye eklenen her adres hemen sunucuya kurulur.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="admin-card address-card">
        <h2 className="admin-card-title">
          <KeyRound size={16} /> Lisans eşitlemesi
        </h2>
        {!state.license_configured ? (
          <p className="admin-card-hint">
            Kurulumda lisans anahtarı tanımlı değil; giriş adresi lisansa
            bildirilmez.
          </p>
        ) : (
          <>
            <p className="admin-card-hint">
              {state.license.follows === true && (
                <>
                  Lisans giriş adreslerine bağlı: yayına alınan adres lisansa da
                  tanımlanır, ayrıca bir işlem gerekmez.
                  {state.license.bound_domain && (
                    <> Lisansın adresi: <strong>{state.license.bound_domain}</strong>.</>
                  )}
                </>
              )}
              {state.license.follows === false && (
                <>
                  Lisans sabit bir adrese bağlı
                  {state.license.bound_domain && (
                    <> (<strong>{state.license.bound_domain}</strong>)</>
                  )}
                  : giriş adresinin değişmesi lisansı etkilemez.
                </>
              )}
              {state.license.follows !== true && state.license.follows !== false
                && 'Liste henüz lisans sunucusuyla eşitlenmedi. İlk yayına almada ya da aşağıdaki düğmeyle eşitlenir.'}
              {' '}Lisans panelinde yapılan geçiş de bir sonraki lisans
              kontrolünde (en geç 10 dakika) buraya gelir.
            </p>
            {state.license.error && (
              <p className="admin-card-hint text-amber-700">
                <TriangleAlert size={13} className="inline mr-1" />
                {state.license_message}
              </p>
            )}
            <div className="address-row">
              <div className="address-meta">
                {state.license.synced_at
                  ? `Son eşitleme: ${formatTime(state.license.synced_at)}`
                  : 'Henüz eşitlenmedi.'}
              </div>
              <div className="address-actions">
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={syncLicense}
                  disabled={busy === 'license-sync'}
                >
                  {busy === 'license-sync'
                    ? <Loader2 size={14} className="animate-spin" />
                    : <RefreshCw size={14} />}
                  {' '}Lisansla eşitle
                </button>
              </div>
            </div>
          </>
        )}
      </div>


      <div className="admin-card address-card">
        <h2 className="admin-card-title">
          <Globe2 size={16} /> Adres listesi
        </h2>
        <p className="admin-card-hint">
          <strong>Giriş adresi</strong> sıradaki alan adıdır; biri yayında
          olur. <strong>Hızlı Giriş</strong> ise oyunculara duyurduğunuz ve
          hiç değişmeyen adrestir (örn. <em>rungiris.com</em>): site açmaz,
          her ziyaretçiyi o an yayında olan adrese yönlendirir. Yayındaki
          adres değiştiğinde Hızlı Giriş'e dokunmanız gerekmez. Yalnızca{' '}
          <strong>bir</strong> Hızlı Giriş tanımlanabilir.
          <br />
          <em>Kontrol Et</em> adresin gerçekten bu kuruluma çözdüğünü sınar
          (DNS, ters vekil, sertifika); sonuç saklanmaz, çünkü engellenen bir
          adresin durumu dakikalar içinde değişir.
        </p>

        <div className="admin-card-hint">
          <RadioTower size={14} className="inline-block mr-1 align-[-2px]" aria-hidden="true" />
          <strong>Hızlı Giriş:</strong>{' '}
          {state.gateway_host ? (
            <>
              {state.gateway_host}
              {' → '}
              {state.active_host || 'yayında adres yok, yönlendirme bekliyor'}
            </>
          ) : 'tanımlı değil — aşağıdan türü “Hızlı Giriş” seçerek ekleyin.'}
        </div>

        <div className="address-form">
          <div className="address-field">
            <label className="admin-label" htmlFor="entry-host">Alan adı</label>
            <input
              id="entry-host"
              className="admin-input"
              value={draft.host}
              onChange={(e) => setDraft({ ...draft, host: e.target.value })}
              placeholder="ornek302.com"
            />
          </div>
          <div className="address-field">
            <label className="admin-label" htmlFor="entry-kind">Tür</label>
            <select
              id="entry-kind"
              className="admin-input admin-input-w-md"
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
            >
              <option value="mirror">{KIND_LABELS.mirror}</option>
              <option value="gateway" disabled={Boolean(state.gateway_host)}>
                {state.gateway_host
                  ? `${KIND_LABELS.gateway} (tanımlı: ${state.gateway_host})`
                  : KIND_LABELS.gateway}
              </option>
            </select>
          </div>
          <div className="address-field">
            <label className="admin-label" htmlFor="entry-label">Not (isteğe bağlı)</label>
            <input
              id="entry-label"
              className="admin-input"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Yedek 2"
            />
          </div>
          <button
            className="admin-btn admin-btn-primary"
            onClick={addDomain}
            disabled={busy === 'add' || !draft.host.trim()}
          >
            <Plus size={15} /> Ekle
          </button>
        </div>

        <div className="address-list">
          {state.domains.length === 0 ? (
            <p className="admin-card-hint">
              Liste boş. Adres eklenene kadar site bugünkü gibi çalışır.
            </p>
          ) : state.domains.map((row) => {
            const isActive = row.id === state.active_id;
            const result = checks[row.id];
            const job = (state.server_jobs || {})[row.host];
            const closed = (state.closed_hosts || []).includes(row.host);
            return (
              <div className="address-row" key={row.id}>
                <div>
                  <div className="address-host">{row.host}</div>
                  <div className="address-meta">
                    {isActive && (
                      <span className="address-pill address-pill-ok">
                        <Check size={12} /> yayında
                      </span>
                    )}
                    {row.status === 'blocked' && (
                      <span className="address-pill address-pill-wait">
                        <Ban size={12} /> engellendi
                      </span>
                    )}
                    {closed && (
                      <span className="address-pill address-pill-wait">
                        <Ban size={12} /> kapatıldı
                      </span>
                    )}
                    {` ${KIND_LABELS[row.kind] || row.kind}`}
                    {row.kind === 'gateway' && state.active_host
                      && ` → ${state.active_host}`}
                    {row.label && ` · ${row.label}`}
                    {row.host === state.current_host && ' · şu an buradasınız'}
                    {job && (
                      <div className="mt-1">
                        <span
                          className={`address-pill ${job.state === 'done'
                            ? 'address-pill-ok' : 'address-pill-wait'}`}
                        >
                          {job.state === 'running'
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Server size={12} />}
                          {' '}{JOB_LABELS[job.state] || job.state}
                          {job.state === 'done' && (job.ssl ? ' · HTTPS' : ' · HTTP')}
                        </span>
                        {job.state !== 'running' && job.message && job.state !== 'done'
                          && <span className="ml-1">{job.message}</span>}
                        {(job.warnings || []).length > 0 && (
                          <div className="text-amber-700">{job.warnings[0]}</div>
                        )}
                        {job.log && (
                          <button
                            type="button"
                            className="ml-1 underline text-[11px]"
                            onClick={() => setOpenLog(openLog === row.id ? '' : row.id)}
                            aria-expanded={openLog === row.id}
                          >
                            {openLog === row.id ? 'Günlüğü gizle' : 'Günlüğü göster'}
                          </button>
                        )}
                        {openLog === row.id && (
                          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px]">
                            {job.log}
                          </pre>
                        )}
                      </div>
                    )}
                    {result && (
                      <div className="mt-1">
                        {result.ok
                          ? `Kuruluma çözüyor${result.cert_expires_at
                            ? ` · sertifika ${new Date(result.cert_expires_at)
                              .toLocaleDateString('tr-TR')}` : ''}`
                          : `Ulaşılamadı: ${result.error}`}
                      </div>
                    )}
                  </div>
                </div>
                <div className="address-actions">
                  {row.kind === 'mirror' && !isActive && (
                    <button
                      className="admin-btn admin-btn-primary"
                      onClick={() => activate(row)}
                      disabled={busy === `activate-${row.id}` || row.status === 'blocked' || closed}
                      title={closed
                        ? 'Kapatılan adres önce Sunucuya kur ile yeniden kurulmalı'
                        : row.status === 'blocked'
                          ? 'Engellenmiş adres yayına alınamaz'
                          : 'Bu adresi yayına al'}
                    >
                      <ArrowRightLeft size={14} /> Yayına al
                    </button>
                  )}
                  {/* Hızlı Giriş tam site kopyası olarak kurulmuşsa (tür
                      sonradan değişti ya da eski kurulum) tanım yönlendirme
                      satırlarıyla yeniden yazılır. */}
                  {row.kind === 'gateway' && state.server?.configured
                    && job?.state === 'done' && !job?.gateway && !job?.imported && (
                    <button
                      className="admin-btn admin-btn-primary"
                      onClick={() => provision(row)}
                      disabled={running || busy === `provision-${row.id}`}
                      title="Sunucudaki tanımı yayındaki adrese yönlendirecek şekilde yeniden yazar"
                    >
                      <RadioTower size={14} /> Yönlendirmeye çevir
                    </button>
                  )}
                  {/* Kurulu adreste düğme çizilmez: yeniden kurmak bir şey
                      kazandırmaz, içe aktarılan adreste ise nginx aynı adı
                      ikinci kez kabul etmez. Yalnızca kurulmamış ya da
                      başarısız/kaldırılmış adres kurulur. */}
                  {state.server?.configured && job?.state !== 'done'
                    && job?.state !== 'running' && (
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => provision(row)}
                      disabled={running || busy === `provision-${row.id}`}
                      title="nginx site tanımını yazar ve isteğe bağlı sertifika alır"
                    >
                      <UploadCloud size={14} /> Sunucuya kur
                    </button>
                  )}
                  {/* Panelin kurduğu ama sertifikası alınamamış adres:
                      DNS yayıldıktan sonra sertifika yeniden denenir. */}
                  {state.server?.configured && job?.state === 'done' && !job?.ssl
                    && !job?.imported && (
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => run(
                        `provision-${row.id}`,
                        () => client.post(`/admin/entry-domains/${row.id}/provision`, { ssl: true }),
                        `${row.host} için sertifika deneniyor`,
                      )}
                      disabled={running || busy === `provision-${row.id}`}
                      title="Let's Encrypt sertifikasını yeniden dener"
                    >
                      <UploadCloud size={14} /> SSL al
                    </button>
                  )}
                  {/* Kaldırma adı sunucudan tamamen siler: panelin dosyası,
                      sitenin kendi tanımındaki ad ve sertifika gider, yerine
                      bağlantıyı kesen bir tanım yazılır (ad hiçbir yerde
                      kalmasa varsayılan siteye düşüp siteyi açardı).
                      Yayındaki ve panelin açık olduğu adres kapatılamaz. */}
                  {!isActive && !closed && job?.state !== 'running'
                    && row.host !== state.current_host && (
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => setPending({ action: 'unprovision', row })}
                      disabled={running || busy === `unprovision-${row.id}`}
                    >
                      <Ban size={14} /> Sunucudan kaldır
                    </button>
                  )}
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={() => check(row)}
                    disabled={busy === `check-${row.id}`}
                  >
                    <RefreshCw size={14} /> Kontrol et
                  </button>
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={() => setStatus(
                      row, row.status === 'blocked' ? 'ready' : 'blocked',
                    )}
                    disabled={busy === `status-${row.id}`}
                  >
                    {row.status === 'blocked'
                      ? (<><Check size={14} /> Kullanılabilir</>)
                      : (<><TriangleAlert size={14} /> Engellendi</>)}
                  </button>
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => askDelete(row)}
                    disabled={busy === `del-${row.id}`}
                  >
                    <Trash2 size={14} /> Sil
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.action === 'unprovision'
          ? `${pending?.row.host} sunucudan kaldırılsın mı?`
          : `${pending?.row.host || ''} silinsin mi?`}
        description={pending?.action === 'unprovision'
          ? 'Adres sunucudan tamamen kaldırılır: bütün nginx tanımlarından '
            + '(sitenin kendi tanımı dahil) çıkarılır, sertifikası silinir ve bu '
            + 'ada gelen bağlantı kesilir. Site bu adreste hiçbir yerden açılmaz, '
            + 'ziyaretçi yayındaki adrese de taşınmaz. Yeniden kullanmak için '
            + '"Sunucuya kur" gerekir.'
          : 'Adres giriş listesinden çıkarılır.'}
        extra={pending?.action === 'delete' ? (
          <label className="mt-1 flex cursor-pointer items-start gap-2 rounded-xl border border-red-400/25 bg-red-500/[0.07] p-3 text-left text-xs">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={closeOnDelete}
              onChange={(event) => setCloseOnDelete(event.target.checked)}
              disabled={pending.row.id === state.active_id
                || pending.row.host === state.current_host}
            />
            <span>
              <b>Sunucudan da kaldır ve adresi kapat.</b>
              {pending.row.id === state.active_id
                ? ' Yayındaki adres kapatılamaz; önce başka bir adresi yayına alın.'
                : pending.row.host === state.current_host
                  ? ' Panele şu an bu adresten bağlısınız; kapatmak için paneli başka bir adresten açın.'
                  : closeOnDelete
                    ? ' Bu adreste site açılmaz.'
                    : ' İşaretlenmezse sunucudaki tanım kalır ve adres listede olmadığı için siteyi tam olarak açmaya devam edebilir.'}
            </span>
          </label>
        ) : null}
        confirmLabel={pending?.action === 'unprovision'
          ? 'Sunucudan kaldır'
          : closeOnDelete ? 'Kaldır ve sil' : 'Yalnızca listeden sil'}
        tone="danger"
        onCancel={() => setPending(null)}
        onConfirm={confirmPending}
      />
    </div>
  );
};

export default EntryDomains;
